import React, { useEffect, useState, useMemo } from "react";
import { transactionsApiService } from "../../../service/admin/transactionsApiService";
import { useLoader } from "../../../base/context/LoaderProvider";
import { ToastTypes, useMessage } from "../../../base/context/MessageProvider";
import { useNavigate, useLocation } from "react-router-dom";
import ExcelTable from "../../../component/library/excelTable";
import { getAuthLocalStorage } from "../../../storage/localStorage";
import moment from "moment";
import "./style.css";

const Transactions = () => {
  const [transactions, setTransactions] = useState([]);
  const [allTransactionData, setAllTransactionData] = useState("");
  const [startDate, setStartDate] = useState("");
  const [type, setType] = useState("");
  const [paymentSource, setPaymentSource] = useState("");
  const [endDate, setEndDate] = useState("");
  const loader = useLoader();
  const message = useMessage();
  const navigate = useNavigate();
  const location = useLocation();
  const [limit, setLimit] = useState(20);
  const [page, setPage] = useState(1);
  const [searchEmail, setSearchEmail] = useState(
    (location.state && location.state.email) || ""
  );
  const [pages, setPages] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
  });
  const [initialLoading, setInitialLoading] = useState(true);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [clearTrigger, setClearTrigger] = useState(0);
  const [exportModal, setExportModal] = useState({ show: false, title: "", message: "", type: "error" });
  const [confirmExport, setConfirmExport] = useState({ show: false, totalItems: 0 });

  const authData = getAuthLocalStorage();
  const currentUserId = authData?.loginHeader || null;

  useEffect(() => {
    if (!startDate && !endDate) {
      const today = new Date();
      const sevenDaysAgo = new Date();
      sevenDaysAgo.setDate(today.getDate() - 7);
      setStartDate(sevenDaysAgo);
      setEndDate(today);
    }
  }, []);

  useEffect(() => {
    fetchTransactions();
  }, [page, limit]);

  useEffect(() => {
    if (location?.state?.email) {
      setSearchEmail(location.state.email);
      setPage(1);
      fetchTransactions();
    }
  }, [location?.state?.email]);

  useEffect(() => {
    if (clearTrigger > 0) {
      fetchTransactions();
    }
  }, [clearTrigger]);

  const buildQueryParams = () => {
    const params = new URLSearchParams();
    if (startDate) params.append("startDate", new Date(startDate).toISOString());
    if (endDate) params.append("endDate", new Date(endDate).toISOString());
    if (type) params.append("type", type);
    if (paymentSource) params.append("paymentSource", paymentSource);
    const trimmedEmail = searchEmail.trim();
    if (trimmedEmail) params.append("email", trimmedEmail);
    return params;
  };

  const fetchTransactions = () => {
    setLoading(true);
    loader.showInlineLoader("Loading...", "medium");

    const params = buildQueryParams();
    params.append("page", page);
    params.append("limit", limit);

    transactionsApiService
      .getTransactions(`?${params.toString()}`)
      .then((res) => {
        loader.hideLoader();
        setLoading(false);
        setInitialLoading(false);
        if (res.status.code === 200) {
          setTransactions(res.data.transactions);
          const totalItems = res.data.totalTransaction ?? res.data.totalTransactions ?? 0;
          setPages({
            currentPage: res.data.currentPage,
            totalPages: res.data.totalPages,
            totalItems: totalItems,
          });
          setPage(res.data.currentPage);
          setAllTransactionData(res.data);
        } else {
          message.showToast(res.error.message, ToastTypes.ERROR);
        }
      })
      .catch((err) => {
        loader.hideLoader();
        setLoading(false);
        setInitialLoading(false);
        message.showToast(err?.message || "Server error", ToastTypes.ERROR);
      });
  };

  const handleOrderById = async (item) => {
    const array = item.description.split(" ");
    const id = array[array.length - 1];
    try {
      const res = await transactionsApiService.fetchOrderDetails(id);
      if (res.status.code === 200) {
        navigate(`/admin/order-management/details/${res?.data?._id}`);
      } else {
        message.showToast(res.error.message, ToastTypes.ERROR);
      }
    } catch (err) {
      message.showToast(err?.message || "Server error", ToastTypes.ERROR);
    }
  };

  const EXPORT_LIMIT = 10000;

  // Professional banking-style export (max 5 rows for testing)
  const handleExportCsv = async () => {
    if (!startDate || !endDate) {
      setExportModal({ show: true, title: "Export Failed", message: "Please select a date range before exporting.", type: "error" });
      return;
    }

    const totalItems = pages.totalItems || 0;

    // If total items exceed export limit, show warning first
    if (totalItems > EXPORT_LIMIT) {
      setConfirmExport({ show: true, totalItems });
      return;
    }

    executeExport();
  };

  const executeExport = async () => {
    setConfirmExport({ show: false, totalItems: 0 });
    setExporting(true);
    try {
      const params = buildQueryParams();
      const res = await transactionsApiService.exportTransactions(`?${params.toString()}`);
      if (res.status.code === 200 && res.data?.transactions?.length > 0) {
        const exportData = res.data.transactions;
        const dateRange = `${moment(startDate).format("DD-MMM-YYYY")}_to_${moment(endDate).format("DD-MMM-YYYY")}`;
        const fileName = `Transaction_Statement_${dateRange}`;

        let csvContent = "";
        csvContent += "IMPORTERR - TRANSACTION STATEMENT\r\n";
        csvContent += `Statement Period: ${moment(startDate).format("DD MMM YYYY")} to ${moment(endDate).format("DD MMM YYYY")}\r\n`;
        csvContent += `Generated On: ${moment().format("DD MMM YYYY, HH:mm:ss")}\r\n`;
        csvContent += `Total Records: ${exportData.length}\r\n`;
        if (type) csvContent += `Transaction Type: ${type}\r\n`;
        if (paymentSource) csvContent += `Payment Source: ${paymentSource}\r\n`;
        if (searchEmail.trim()) csvContent += `Filtered By Email: ${searchEmail.trim()}\r\n`;
        csvContent += "\r\n";

        const headers = [
          "S.No.", "Transaction ID", "Seller ID", "Full Name", "Email",
          "Type", "Payment Source", "Payment ID", "Amount (INR)",
          "Previous Balance (INR)", "Balance (INR)", "Description", "Date & Time"
        ];
        csvContent += headers.map(h => `"${h}"`).join(",") + "\r\n";

        exportData.forEach((item, index) => {
          const txDate = item?.date || item?.created_at || item?.createdAt || item?.updated_at || null;
          const balance = Number(item?.balance) || 0;
          const amount = Number(item?.amount) || 0;
          const previousBalance = balance - amount;
          const row = [
            index + 1,
            item?.transactionId || "-",
            item?.userId?.uniqueId || "-",
            `${item?.userId?.firstName || ""} ${item?.userId?.lastName || ""}`.trim() || "-",
            item?.userId?.email || "-",
            item?.type || "-",
            item?.paymentId ? "Razorpay" : (item?.paymentSource || "wallet"),
            item?.paymentId || "-",
            amount.toFixed(2),
            previousBalance.toFixed(2),
            balance.toFixed(2),
            (item?.description || "-").replace(/"/g, '""'),
            txDate ? moment(txDate).format("DD/MM/YYYY HH:mm:ss") : "-",
          ];
          csvContent += row.map(v => `"${v}"`).join(",") + "\r\n";
        });

        csvContent += "\r\n";
        csvContent += `"--- END OF STATEMENT ---"\r\n`;

        const blob = new Blob(["\uFEFF" + csvContent], { type: "text/csv;charset=utf-8;" });
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = `${fileName}.csv`;
        link.click();
        URL.revokeObjectURL(link.href);

        setExportModal({ show: true, title: "Export Successful", message: `Successfully exported ${exportData.length} transactions.`, type: "success" });
      } else {
        setExportModal({ show: true, title: "No Data Found", message: "No transactions found for the selected filters. Please adjust your filters and try again.", type: "error" });
      }
    } catch (err) {
      setExportModal({ show: true, title: "Export Failed", message: err?.message || "Something went wrong while exporting. Please try again.", type: "error" });
    }
    setExporting(false);
  };

  const handleSearch = () => {
    setPage(1);
    fetchTransactions();
  };

  const handleClear = () => {
    const today = new Date();
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(today.getDate() - 7);

    setType("");
    setPaymentSource("");
    setStartDate(sevenDaysAgo);
    setEndDate(today);
    setSearchEmail("");
    setPage(1);
    setClearTrigger((prev) => prev + 1);
  };

  // Table rows
  const tableRows = useMemo(() => {
    return (transactions || []).map((item, index) => {
      const txDate = item?.date || item?.created_at || item?.createdAt || item?.updated_at || null;
      const balance = Number(item?.balance) || 0;
      const amount = Number(item?.amount) || 0;
      const previousBalance = balance - amount;
      return {
        _id: item._id || index,
        serialNumber: (page - 1) * limit + index + 1,
        raw: item,
        sellerId: item?.userId?.uniqueId || "-",
        fullName: `${item?.userId?.firstName || ""} ${item?.userId?.lastName || ""}`.trim() || "-",
        email: item?.userId?.email || "-",
        type: item?.type || "-",
        paymentSourceDisplay: item?.paymentId ? "Razorpay" : (item?.paymentSource === "credit" ? "Credit" : "Wallet"),
        transactionId: item?.transactionId || "-",
        paymentId: item?.paymentId || "-",
        amount: amount,
        previousBalance: previousBalance,
        balance: balance,
        description: item?.description || "-",
        date: txDate ? moment(txDate).format("DD MMM YYYY, HH:mm") : "-",
        isOrderType: item?.type === "Order" || item?.type === "Refund",
      };
    });
  }, [transactions, page, limit]);

  const formatAmount = (value) => {
    const num = Number(value) || 0;
    return num.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  // Columns
  const columns = useMemo(() => [
    {
      key: "serialNumber",
      label: "#",
      width: 50,
      render: (value) => <span className="txnpg-serial">{value}</span>,
    },
    {
      key: "transactionId",
      label: "Transaction ID",
      width: 180,
      render: (value) => <span className="txnpg-txn-id" title={value}>{value}</span>,
    },
    {
      key: "sellerId",
      label: "Seller ID",
      width: 120,
      render: (value) => <span className="txnpg-seller-id">{value}</span>,
    },
    {
      key: "fullName",
      label: "Full Name",
      width: 140,
      render: (value) => <span className="txnpg-name">{value}</span>,
    },
    {
      key: "email",
      label: "Email",
      width: 180,
      render: (value) => <span className="txnpg-email">{value}</span>,
    },
    {
      key: "type",
      label: "Type",
      width: 110,
      render: (value) => {
        const displayName = value === "Wallet-Deduct" ? "Wallet Deduct"
          : value === "Credit-Add" ? "Credit Add"
          : value === "Credit-Deduct" ? "Credit Deduct"
          : value;
        return (
          <span className={`txnpg-type-badge txnpg-type-${(value || '').toLowerCase()}`}>
            {displayName}
          </span>
        );
      },
    },
    {
      key: "paymentSourceDisplay",
      label: "Source",
      width: 90,
      render: (value) => {
        const cls = value === "Razorpay" ? "txnpg-source-razorpay"
          : value === "Credit" ? "txnpg-source-credit"
          : "txnpg-source-wallet";
        return <span className={`txnpg-source-badge ${cls}`}>{value}</span>;
      },
    },
    {
      key: "paymentId",
      label: "Payment ID",
      width: 160,
      render: (value) => <span className="txnpg-payment-id" title={value}>{value !== "-" ? value : "-"}</span>,
    },
    {
      key: "amount",
      label: "Amount",
      width: 120,
      render: (value) => <span className="txnpg-amount">₹{formatAmount(value)}</span>,
    },
    {
      key: "previousBalance",
      label: "Previous Balance",
      width: 130,
      render: (value) => <span className="txnpg-prev-balance">₹{formatAmount(value)}</span>,
    },
    {
      key: "balance",
      label: "Balance",
      width: 120,
      render: (value) => <span className="txnpg-balance">₹{formatAmount(value)}</span>,
    },
    {
      key: "description",
      label: "Description",
      width: 250,
      wrap: true,
      render: (value, row) => (
        row.isOrderType ? (
          <span className="txnpg-desc-link" onClick={() => handleOrderById(row.raw)}>{value}</span>
        ) : (
          <span className="txnpg-desc">{value}</span>
        )
      ),
    },
    {
      key: "date",
      label: "Date",
      width: 150,
      render: (value) => <span className="txnpg-date">{value}</span>,
    },
  ], []);

  const startItem = (page - 1) * limit + 1;
  const endItem = Math.min(page * limit, pages.totalItems);

  // Toolbar right actions
  const toolbarRightActions = (
    <div className="txnpg-toolbar-right">
      <div className="txnpg-rows-picker">
        <span>Rows:</span>
        <select value={limit} onChange={(e) => { setLimit(Number(e.target.value)); setPage(1); }}>
          {[10, 20, 50, 100].map(n => <option key={n} value={n}>{n}</option>)}
        </select>
      </div>
    </div>
  );

  return (
    <div className="txnpg-page">
      {/* Stats Cards */}
      <div className="txnpg-stats-row">
        <div className="txnpg-stat-card">
          <span className="txnpg-stat-label">Wallet Amount</span>
          <span className="txnpg-stat-value txnpg-stat-wallet">₹{formatAmount(allTransactionData?.walletAmount)}</span>
        </div>
        <div className="txnpg-stat-card">
          <span className="txnpg-stat-label">Total Deposit</span>
          <span className="txnpg-stat-value txnpg-stat-deposit">₹{formatAmount(allTransactionData?.totalAmountsByType?.Deposit)}</span>
        </div>
        <div className="txnpg-stat-card">
          <span className="txnpg-stat-label">Total Order</span>
          <span className="txnpg-stat-value txnpg-stat-order">₹{formatAmount(allTransactionData?.totalAmountsByType?.Order)}</span>
        </div>
        <div className="txnpg-stat-card">
          <span className="txnpg-stat-label">Total Membership</span>
          <span className="txnpg-stat-value txnpg-stat-membership">₹{formatAmount(allTransactionData?.totalAmountsByType?.Membership)}</span>
        </div>
        <div className="txnpg-stat-card">
          <span className="txnpg-stat-label">Refund Balance</span>
          <span className="txnpg-stat-value txnpg-stat-refund">₹{formatAmount(allTransactionData?.totalAmountsByType?.Refund)}</span>
        </div>
      </div>

      {/* Separate Filter Section */}
      <div className="txnpg-filter-section">
        <div className="txnpg-filter-header">
          <span className="txnpg-filter-title">Filters</span>
          <div className="txnpg-filter-actions">
            <button className="txnpg-btn txnpg-btn-primary" onClick={handleSearch}>Search</button>
            <button className="txnpg-btn txnpg-btn-outline" onClick={handleClear}>Clear</button>
            <button className="txnpg-btn txnpg-btn-dark" onClick={handleExportCsv} disabled={exporting}>
              {exporting ? "Exporting..." : "Export CSV"}
            </button>
          </div>
        </div>
        <div className="txnpg-filter-grid">
          <div className="txnpg-filter-item">
            <label className="txnpg-filter-label">Transaction Type</label>
            <select className="txnpg-filter-select" value={type} onChange={(e) => setType(e.target.value)}>
              <option value="">All Types</option>
              <option value="Deposit">Deposit</option>
              <option value="Order">Order</option>
              <option value="Membership">Membership</option>
              <option value="Refund">Refund</option>
              <option value="Wallet-Deduct">Wallet Deduct</option>
              <option value="Credit-Add">Credit Add</option>
              <option value="Credit-Deduct">Credit Deduct</option>
              <option value="Failed-Payment">Failed Payment</option>
            </select>
          </div>
          <div className="txnpg-filter-item">
            <label className="txnpg-filter-label">Payment Source</label>
            <select className="txnpg-filter-select" value={paymentSource} onChange={(e) => setPaymentSource(e.target.value)}>
              <option value="">All Sources</option>
              <option value="wallet">Wallet</option>
              <option value="credit">Credit</option>
              <option value="razorpay">Razorpay</option>
            </select>
          </div>
          <div className="txnpg-filter-item">
            <label className="txnpg-filter-label">From Date</label>
            <input
              type="date"
              className="txnpg-filter-input"
              value={startDate ? moment(startDate).format("YYYY-MM-DD") : ""}
              onChange={(e) => setStartDate(e.target.value ? new Date(e.target.value) : "")}
            />
          </div>
          <div className="txnpg-filter-item">
            <label className="txnpg-filter-label">To Date</label>
            <input
              type="date"
              className="txnpg-filter-input"
              value={endDate ? moment(endDate).format("YYYY-MM-DD") : ""}
              onChange={(e) => setEndDate(e.target.value ? new Date(e.target.value) : "")}
            />
          </div>
          <div className="txnpg-filter-item txnpg-filter-item-wide">
            <label className="txnpg-filter-label">Search by Email</label>
            <input
              type="text"
              className="txnpg-filter-input"
              placeholder="Enter email address..."
              value={searchEmail}
              onChange={(e) => setSearchEmail(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSearch()}
            />
          </div>
        </div>
      </div>

      <div className="txnpg-table-section">
        <ExcelTable
          data={tableRows}
          columns={columns}
          isLoading={initialLoading || loading}
          loadingText="Loading transactions..."
          showToolbarExport={false}
          showToolbarFilter={false}
          showToolbarColumns={true}
          showToolbarFullscreen={true}
          toolbarRightActions={toolbarRightActions}
          selectable={false}
          sortable={true}
          resizable={true}
          showToolbar={true}
          title="Transactions"
          userId={currentUserId}
        />

        {transactions.length > 0 && pages.totalPages > 1 && (
          <div className="txnpg-pagination">
            <div className="txnpg-pagination-info">Showing {startItem} to {endItem} of {pages.totalItems}</div>
            <div className="txnpg-pagination-controls">
              <button className="txnpg-page-btn" disabled={page <= 1} onClick={() => setPage(page - 1)}>‹</button>
              {Array.from({ length: Math.min(5, pages.totalPages) }, (_, i) => {
                let pageNum;
                if (pages.totalPages <= 5) pageNum = i + 1;
                else if (page <= 3) pageNum = i + 1;
                else if (page >= pages.totalPages - 2) pageNum = pages.totalPages - 4 + i;
                else pageNum = page - 2 + i;
                return (
                  <button key={pageNum} className={`txnpg-page-btn ${page === pageNum ? "txnpg-page-btn-active" : ""}`} onClick={() => setPage(pageNum)}>
                    {pageNum}
                  </button>
                );
              })}
              {pages.totalPages > 5 && page < pages.totalPages - 2 && (
                <>
                  <span className="txnpg-page-dots">...</span>
                  <button className="txnpg-page-btn" onClick={() => setPage(pages.totalPages)}>{pages.totalPages}</button>
                </>
              )}
              <button className="txnpg-page-btn" disabled={page >= pages.totalPages} onClick={() => setPage(page + 1)}>›</button>
            </div>
          </div>
        )}
      </div>

      {/* Export Warning/Confirm Modal */}
      {confirmExport.show && (
        <div className="txnpg-modal-overlay" onClick={() => setConfirmExport({ show: false, totalItems: 0 })}>
          <div className="txnpg-modal" onClick={(e) => e.stopPropagation()}>
            <div className="txnpg-modal-icon txnpg-modal-icon-warning">!</div>
            <h3 className="txnpg-modal-title">Large Export Warning</h3>
            <p className="txnpg-modal-message">
              Your current filter has <strong>{confirmExport.totalItems.toLocaleString('en-IN')}</strong> transactions, 
              but only the first <strong>{EXPORT_LIMIT.toLocaleString('en-IN')}</strong> rows will be exported.
            </p>
            <div className="txnpg-modal-hints">
              <p className="txnpg-modal-hint-title">To export all records, try:</p>
              <ul className="txnpg-modal-hint-list">
                <li>Narrow the date range</li>
                <li>Filter by transaction type or payment source</li>
                <li>Search by a specific email</li>
              </ul>
            </div>
            <div className="txnpg-modal-actions">
              <button className="txnpg-btn txnpg-btn-outline" onClick={() => setConfirmExport({ show: false, totalItems: 0 })}>Cancel</button>
              <button className="txnpg-btn txnpg-btn-primary" onClick={executeExport}>Export Anyway</button>
            </div>
          </div>
        </div>
      )}

      {/* Export Result Modal */}
      {exportModal.show && (
        <div className="txnpg-modal-overlay" onClick={() => setExportModal({ ...exportModal, show: false })}>
          <div className="txnpg-modal" onClick={(e) => e.stopPropagation()}>
            <div className={`txnpg-modal-icon ${exportModal.type === "success" ? "txnpg-modal-icon-success" : "txnpg-modal-icon-error"}`}>
              {exportModal.type === "success" ? "✓" : "✕"}
            </div>
            <h3 className="txnpg-modal-title">{exportModal.title}</h3>
            <p className="txnpg-modal-message">{exportModal.message}</p>
            <button className="txnpg-btn txnpg-btn-primary txnpg-modal-btn" onClick={() => setExportModal({ ...exportModal, show: false })}>
              OK
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Transactions;
